import { useCallback, useEffect, useState } from "react";
import * as Team from "src/state/modules/team";
import * as Event from "src/state/modules/event";
import PageLoader1 from "src/components/atoms/PageLoader/PageLoader";
import { useOpponentGameState, UseGameStateResult } from "src/components/util/useGameState";
import { useGetTeamEventOpponentSeasonStatsQuery } from "src/state/modules/api";
import { BoxScoreView, GetHitterSeason } from "./BoxScore";
import { PlaysView } from "./PlaysPage";
import BookCompare from "./BookCompare";
import Styles from "./OpponentGameModal.module.scss";

interface Props {
  team: Team.Team;
  event: Event.Event;
  // Our own scorebook for this game (already built by the Box Score tab), so the Compare tab can
  // diff the two books without re-fetching/re-building our side.
  ourGameState: UseGameStateResult;
  onClose: () => void;
}

type Tab = "box" | "plays" | "compare";

/**
 * Shows the *opponent's* own scorebook for this game in a dismissable modal. In GameChanger each
 * team keeps its own book, so this replays the opponent's (progenitor team's) stream through the
 * same sabertooth pipeline as our tabs and renders it with {@link BoxScoreView} / {@link PlaysView}.
 * The opponent stream only fetches while this modal is mounted ({ enabled: true }). A 404 — the
 * opponent never tracked a matching game — surfaces as `isError` → the "not available" state.
 */
export default function OpponentGameModal({ team, event, ourGameState, onClose }: Props) {
  const [tab, setTab] = useState<Tab>("box");
  const opponentName = event.opponentTeam?.name ?? "Opponent";
  const { built, state, players, isLoading, isError, opponentTeamId } = useOpponentGameState(team, event.id, {
    enabled: true,
  });

  // Opponent scouting cues for the box score. Here the players carry the opponent's real roster ids
  // (the progenitor team), so we correlate to season-stats by exact id — no name matching needed.
  const { data: opponentSeason } = useGetTeamEventOpponentSeasonStatsQuery({ teamID: team.id, eventID: event.id });
  const getHitterSeason = useCallback<GetHitterSeason>(
    (panelTeamId, playerId) => {
      if (!opponentSeason || panelTeamId !== opponentTeamId) return undefined;
      return opponentSeason.players[playerId];
    },
    [opponentSeason, opponentTeamId]
  );

  // Both books must be built to compare. Our side comes from the Box Score tab; the modal only
  // opens from a rendered box score, so this is normally already present.
  const canCompare = !!ourGameState.built && !!ourGameState.state && !!opponentTeamId;

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  // Label the two sides from the opponent's perspective: their (progenitor) id is the opponent; the
  // other id in their stream is their record of us.
  const teamName = (id: string) => (id === opponentTeamId ? opponentName : team.name);

  const ready = !!built && !!state;

  return (
    <div className={Styles.overlay} onClick={onClose} role="presentation">
      <div
        className={Styles.card}
        role="dialog"
        aria-modal="true"
        aria-label={`${opponentName} scorebook`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={Styles.header}>
          <button type="button" className={Styles.close} onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
          <div className={Styles.titleBlock}>
            <div className={Styles.title}>{opponentName}</div>
            <div className={Styles.subtitle}>Opponent’s scorebook</div>
          </div>
        </div>

        {ready && (
          <div className={Styles.tabs} role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === "box"}
              className={tab === "box" ? Styles.tabActive : Styles.tab}
              onClick={() => setTab("box")}
            >
              Box Score
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "plays"}
              className={tab === "plays" ? Styles.tabActive : Styles.tab}
              onClick={() => setTab("plays")}
            >
              Plays
            </button>
            {canCompare && (
              <button
                type="button"
                role="tab"
                aria-selected={tab === "compare"}
                className={tab === "compare" ? Styles.tabActive : Styles.tab}
                onClick={() => setTab("compare")}
              >
                Compare
              </button>
            )}
          </div>
        )}

        <div className={Styles.body}>
          {isLoading && !state ? (
            <PageLoader1 />
          ) : isError || !ready ? (
            <div className={Styles.empty}>{opponentName} didn’t track this game in GameChanger.</div>
          ) : tab === "box" ? (
            <BoxScoreView built={built} state={state} players={players} teamName={teamName} getHitterSeason={getHitterSeason} />
          ) : tab === "plays" ? (
            <PlaysView built={built} state={state} players={players} teamName={teamName} />
          ) : canCompare && ourGameState.built && ourGameState.state ? (
            <BookCompare
              ourBuilt={ourGameState.built}
              ourState={ourGameState.state}
              ourPlayers={ourGameState.players}
              theirBuilt={built}
              theirState={state}
              theirPlayers={players}
              ourTeamId={team.id}
              opponentTeamId={opponentTeamId!}
              ourName={team.name}
              opponentName={opponentName}
            />
          ) : (
            <div className={Styles.empty}>Comparison isn’t available for this game.</div>
          )}
        </div>
      </div>
    </div>
  );
}
